import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import BackgroundEffects from '../../../components/layout/BackgroundEffects/BackgroundEffects'
import Header from '../../../components/layout/Header/Header'
import Card from '../../../components/common/Card/Card'
import Button from '../../../components/common/Button/Button'
import { getCurrentUser, logout, getUserData } from '../../../services/authService'
import { getTotalWorkedHours } from '../../../services/attendanceService'
import {
  getTimesheetsByEmployee,
  getPendingTimesheets,
  getAllTimesheets,
  getAllEmployees,
  createTimesheet,
  updateTimesheet,
  approveTimesheet,
  rejectTimesheet,
  cancelTimesheet,
  getTotalAllocatedHours,
  getTodayDate,
  formatDate,
  exportTimesheetsToCSV
} from '../../../services/timesheetService'
import TimesheetCard from '../components/TimesheetCard'
import TimesheetEntryModal from '../components/TimesheetEntryModal'
import RejectModal from '../components/RejectModal'
import TimesheetSummary from '../components/TimesheetSummary'

const Timesheet = () => {
  const navigate = useNavigate()

  // Auth & User State
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Timesheet State
  const [myEntries, setMyEntries] = useState([])
  const [pendingEntries, setPendingEntries] = useState([])
  const [allEntries, setAllEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Unallocated hours state
  const [unallocatedHours, setUnallocatedHours] = useState(0)
  const [showUnallocatedAlert, setShowUnallocatedAlert] = useState(false)

  // Filter State
  const [dateFilter, setDateFilter] = useState('last30') // For employee view
  const [statusFilter, setStatusFilter] = useState('all') // For admin view
  const [employeeFilter, setEmployeeFilter] = useState('all')
  const [employees, setEmployees] = useState([])

  // Modal State
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState(null)
  const [rejectingEntry, setRejectingEntry] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Export State
  const [isExporting, setIsExporting] = useState(false)

  // Monthly View State (Admin only)
  const [viewMode, setViewMode] = useState('list') // 'list' | 'monthly'
  const [expandedRows, setExpandedRows] = useState(new Set())

  // Load user data and entries
  useEffect(() => {
    loadUserAndEntries()
  }, [])

  const loadUserAndEntries = async () => {
    try {
      setLoading(true)
      setError('')

      const currentUser = getCurrentUser()
      if (!currentUser) {
        navigate('/login')
        return
      }

      setUser(currentUser)

      // Get user data from Firestore
      const userDataResult = await getUserData(currentUser.uid)

      if (!userDataResult.success) {
        setError('Failed to load user data')
        setLoading(false)
        return
      }

      setUserData(userDataResult.data)

      // Check if user is admin
      const adminStatus =
        userDataResult.data.role === 'super_admin' ||
        userDataResult.data.role === 'admin'
      setIsAdmin(adminStatus)

      // Load entries based on role
      if (adminStatus) {
        await loadAdminData(userDataResult.data)
      } else {
        await loadEmployeeData(userDataResult.data.employeeId)
      }
    } catch (error) {
      console.error('Load error:', error)
      setError('An error occurred while loading data')
    } finally {
      setLoading(false)
    }
  }

  const loadEmployeeData = async (employeeId) => {
    // Load own entries
    const result = await getTimesheetsByEmployee(employeeId)
    if (result.success) {
      setMyEntries(result.data)
    } else {
      setError(result.error || 'Failed to load timesheet entries')
    }

    // Check unallocated hours for today
    await checkUnallocatedHours(employeeId)
  }

  const loadAdminData = async (userData) => {
    // Load pending entries
    const pendingResult = await getPendingTimesheets(userData)
    if (pendingResult.success) {
      setPendingEntries(pendingResult.data)
    }

    // Load all entries
    const allResult = await getAllTimesheets(userData)
    if (allResult.success) {
      setAllEntries(allResult.data)
    }

    // Load employees for filter
    const employeesResult = await getAllEmployees()
    if (employeesResult.success) {
      setEmployees(employeesResult.data)
    }

    // Still load own entries for admin
    const myResult = await getTimesheetsByEmployee(userData.employeeId)
    if (myResult.success) {
      setMyEntries(myResult.data)
    }

    // Check unallocated hours
    await checkUnallocatedHours(userData.employeeId)
  }

  const checkUnallocatedHours = async (employeeId) => {
    try {
      const todayDate = getTodayDate()

      // Get worked hours
      const workedResult = await getTotalWorkedHours(employeeId, todayDate)

      if (!workedResult.success || !workedResult.hasCheckOut) {
        setShowUnallocatedAlert(false)
        return
      }

      const workedHours = workedResult.hours

      // Get allocated hours
      const allocatedResult = await getTotalAllocatedHours(employeeId, todayDate)

      if (!allocatedResult.success) {
        setShowUnallocatedAlert(false)
        return
      }

      const allocatedHours = allocatedResult.hours
      const unallocated = workedHours - allocatedHours

      if (unallocated > 0.5) { // Threshold 0.5h
        setUnallocatedHours(unallocated)
        setShowUnallocatedAlert(true)
      } else {
        setShowUnallocatedAlert(false)
      }
    } catch (error) {
      console.error('Check unallocated hours error:', error)
    }
  }

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) {
      navigate('/login')
    }
  }

  const handleBack = () => {
    navigate('/dashboard')
  }

  const handleOpenEntryModal = () => {
    setEditingEntry(null)
    setIsEntryModalOpen(true)
  }

  const handleCloseEntryModal = () => {
    if (!isSubmitting) {
      setEditingEntry(null)
      setIsEntryModalOpen(false)
    }
  }

  const handleSubmitEntry = async (formData) => {
    setIsSubmitting(true)
    setError('')
    setSuccessMessage('')

    try {
      let result

      if (editingEntry) {
        // Update existing entry
        result = await updateTimesheet(editingEntry.id, formData, {
          employeeId: userData.employeeId,
          uid: user.uid,
          fullName: userData.fullName,
          email: userData.email
        })
      } else {
        // Create new entry
        result = await createTimesheet(formData, {
          employeeId: userData.employeeId,
          uid: user.uid,
          fullName: userData.fullName,
          email: userData.email,
          reportsTo: userData.reportsTo,
          reportsToName: userData.reportsToName
        })
      }

      if (result.success) {
        // Reload data
        if (isAdmin) {
          await loadAdminData(userData)
        } else {
          await loadEmployeeData(userData.employeeId)
        }

        setSuccessMessage(editingEntry ? 'Timesheet entry updated successfully!' : 'Timesheet entry added successfully!')
        handleCloseEntryModal()

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to save timesheet entry')
        setTimeout(() => setError(''), 5000)
      }
    } catch (error) {
      console.error('Submit entry error:', error)
      setError('An error occurred while saving the entry')
      setTimeout(() => setError(''), 5000)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditEntry = (entry) => {
    setEditingEntry(entry)
    setIsEntryModalOpen(true)
  }

  const handleApprove = async (entryId) => {
    setError('')
    setSuccessMessage('')

    try {

      const result = await approveTimesheet(entryId, {
        uid: user.uid,
        employeeId: userData.employeeId,
        role: userData.role,
        fullName: userData.fullName,
        email: userData.email
      })

      if (result.success) {
        // Reload admin data
        await loadAdminData(userData)
        setSuccessMessage('Timesheet entry approved successfully!')
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to approve timesheet entry')
        setTimeout(() => setError(''), 5000)
      }
    } catch (error) {
      console.error('Approve error:', error)
      setError('An error occurred while approving the entry')
      setTimeout(() => setError(''), 5000)
    }
  }

  const handleOpenRejectModal = (entry) => {
    setRejectingEntry(entry)
    setIsRejectModalOpen(true)
  }

  const handleCloseRejectModal = () => {
    setIsRejectModalOpen(false)
    setRejectingEntry(null)
  }

  const handleSubmitReject = async (comments) => {
    setIsSubmitting(true)
    setError('')
    setSuccessMessage('')

    try {
      const result = await rejectTimesheet(rejectingEntry.id, {
        uid: user.uid,
        employeeId: userData.employeeId,
        role: userData.role,
        fullName: userData.fullName,
        email: userData.email
      }, comments)

      if (result.success) {
        // Reload admin data
        await loadAdminData(userData)
        setSuccessMessage('Timesheet entry rejected successfully!')
        handleCloseRejectModal()
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to reject timesheet entry')
      }
    } catch (error) {
      console.error('Reject error:', error)
      setError('An error occurred while rejecting the entry')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = async (entryId) => {
    setError('')
    setSuccessMessage('')

    try {
      const result = await cancelTimesheet(entryId)

      if (result.success) {
        // Reload data
        if (isAdmin) {
          await loadAdminData(userData)
        } else {
          await loadEmployeeData(userData.employeeId)
        }

        setSuccessMessage('Timesheet entry cancelled successfully!')
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to cancel timesheet entry')
        setTimeout(() => setError(''), 5000)
      }
    } catch (error) {
      console.error('Cancel error:', error)
      setError('An error occurred while cancelling the entry')
      setTimeout(() => setError(''), 5000)
    }
  }

  const handleExportCSV = async () => {
    setError('')
    setSuccessMessage('')

    // Check if there are entries to export
    if (filteredAllEntries.length === 0) {
      setError('No entries to export')
      setTimeout(() => setError(''), 3000)
      return
    }

    setIsExporting(true)

    try {
      const result = exportTimesheetsToCSV(filteredAllEntries)

      if (result.success) {
        setSuccessMessage(`Exported ${result.count} timesheet ${result.count === 1 ? 'entry' : 'entries'} successfully!`)
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to export timesheets')
        setTimeout(() => setError(''), 5000)
      }
    } catch (error) {
      console.error('Export error:', error)
      setError('An error occurred while exporting timesheets')
      setTimeout(() => setError(''), 5000)
    } finally {
      setIsExporting(false)
    }
  }

  // Filter entries based on date filter (employee view)
  const getFilteredMyEntries = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    let cutoffDate = new Date(today)

    switch (dateFilter) {
      case 'last7':
        cutoffDate.setDate(today.getDate() - 7)
        break
      case 'last30':
        cutoffDate.setDate(today.getDate() - 30)
        break
      case 'last90':
        cutoffDate.setDate(today.getDate() - 90)
        break
      default:
        return myEntries
    }

    const cutoffDateStr = cutoffDate.toISOString().split('T')[0]

    return myEntries.filter(entry => entry.date >= cutoffDateStr)
  }

  // Filter all entries (admin view)
  const getFilteredAllEntries = () => {
    let filtered = [...allEntries]

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(entry => entry.status === statusFilter)
    }

    // Filter by employee
    if (employeeFilter !== 'all') {
      filtered = filtered.filter(entry => entry.employeeId === employeeFilter)
    }

    return filtered
  }

  const filteredMyEntries = getFilteredMyEntries()
  const filteredAllEntries = isAdmin ? getFilteredAllEntries() : []

  // Group entries by date
  const groupEntriesByDate = (entries) => {
    const groups = {}

    entries.forEach(entry => {
      if (!groups[entry.date]) {
        groups[entry.date] = []
      }
      groups[entry.date].push(entry)
    })

    // Sort dates descending
    const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a))

    return sortedDates.map(date => ({
      date,
      entries: groups[date],
      totalHours: groups[date].reduce((sum, entry) => sum + (entry.hours || 0), 0)
    }))
  }

  // Group entries by month (for admin monthly view)
  const groupEntriesByMonth = (entries) => {
    const monthGroups = {}

    entries.forEach(entry => {
      if (!entry.date) return

      // Extract YYYY-MM from date
      const monthKey = entry.date.substring(0, 7) // "2025-10"

      if (!monthGroups[monthKey]) {
        monthGroups[monthKey] = {}
      }

      // Group by employee within month
      const employeeId = entry.employeeId || 'unknown'
      if (!monthGroups[monthKey][employeeId]) {
        monthGroups[monthKey][employeeId] = {
          employeeId,
          employeeName: entry.employeeName || 'Unknown Employee',
          totalHours: 0,
          entries: []
        }
      }

      monthGroups[monthKey][employeeId].totalHours += entry.hours || 0
      monthGroups[monthKey][employeeId].entries.push(entry)
    })

    // Convert to array and sort
    const monthsArray = Object.keys(monthGroups).sort((a, b) => b.localeCompare(a)) // Newest first

    return monthsArray.map(monthKey => {
      // Convert month key to display format
      const [year, month] = monthKey.split('-')
      const monthDate = new Date(year, parseInt(month) - 1, 1)
      const monthDisplay = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

      // Get employees array and sort alphabetically
      const employees = Object.values(monthGroups[monthKey]).sort((a, b) =>
        a.employeeName.localeCompare(b.employeeName)
      )

      return {
        monthKey,
        monthDisplay,
        employees
      }
    })
  }

  // Toggle expand/collapse for monthly view
  const handleToggleRow = (monthKey, employeeId) => {
    const rowKey = `${monthKey}-${employeeId}`
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(rowKey)) {
      newExpanded.delete(rowKey)
    } else {
      newExpanded.add(rowKey)
    }
    setExpandedRows(newExpanded)
  }

  const isRowExpanded = (monthKey, employeeId) => {
    return expandedRows.has(`${monthKey}-${employeeId}`)
  }

  const groupedEntries = groupEntriesByDate(filteredMyEntries)
  const monthlyGroupedEntries = isAdmin ? groupEntriesByMonth(filteredAllEntries) : []

  // Loading State
  if (loading) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Timesheet"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-spinner fa-spin"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-primary)',
              marginBottom: '1rem'
            }}
          ></i>
          <p style={{ color: 'var(--psyber-text-secondary)' }}>Loading timesheets...</p>
        </Card>
      </div>
    )
  }

  // Error State (Critical Error)
  if (error && myEntries.length === 0 && !successMessage) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Timesheet"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-exclamation-triangle"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-error)',
              marginBottom: '1rem'
            }}
          ></i>
          <h3 style={{ marginBottom: '1rem', color: 'var(--psyber-error)' }}>Error</h3>
          <p style={{ color: 'var(--psyber-text-secondary)', marginBottom: '2rem' }}>{error}</p>
          <Button variant="primary" icon="fas fa-redo" onClick={loadUserAndEntries}>
            Retry
          </Button>
        </Card>
      </div>
    )
  }

  // Empty State
  const EmptyState = () => (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <i
        className="fas fa-business-time"
        style={{
          fontSize: '4rem',
          color: 'var(--psyber-text-muted)',
          marginBottom: '1.5rem',
          display: 'block'
        }}
      ></i>
      <h3 style={{ marginBottom: '0.5rem', color: 'var(--psyber-text)' }}>
        No Timesheet Entries Yet
      </h3>
      <p style={{ color: 'var(--psyber-text-secondary)' }}>
        Click "Add Entry" to start tracking your work
      </p>
    </div>
  )

  return (
    <div className="psyber-container psyber-portal-container">
      <BackgroundEffects />

      <Header
        user={userData || user}
        onLogout={handleLogout}
        showBackButton={true}
        onBack={handleBack}
        title="Timesheet"
      />

      {/* Success Message */}
      {successMessage && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-success-bg)',
            border: '2px solid var(--psyber-success)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-check-circle" style={{ color: 'var(--psyber-success)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-success)', fontWeight: 600 }}>{successMessage}</p>
          </div>
        </Card>
      )}

      {/* Error Message */}
      {error && (myEntries.length > 0 || isAdmin) && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-error-bg)',
            border: '2px solid var(--psyber-error)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-exclamation-circle" style={{ color: 'var(--psyber-error)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</p>
          </div>
        </Card>
      )}

      {/* Unallocated Hours Alert (Employee View) */}
      {!isAdmin && showUnallocatedAlert && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-warning-bg)',
            border: '2px solid var(--psyber-warning)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-exclamation-triangle" style={{ color: 'var(--psyber-warning)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-text)', fontWeight: 600 }}>
              ⚠️ You have {unallocatedHours.toFixed(1)} unallocated hours from {formatDate(getTodayDate())}. Please add timesheet entries!
            </p>
          </div>
        </Card>
      )}

      {/* Action Button Card */}
      <Card style={{ marginBottom: '2rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem'
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--psyber-font-size-2xl)',
                color: 'var(--psyber-text)'
              }}
            >
              Track Your Work Hours
            </h2>
            <p
              style={{
                margin: '0.5rem 0 0 0',
                fontSize: 'var(--psyber-font-size-sm)',
                color: 'var(--psyber-text-secondary)'
              }}
            >
              {isAdmin
                ? 'Add your own timesheet entries or review team submissions.'
                : 'Log your daily work hours by project and task.'}
            </p>
          </div>

          <Button
            variant="primary"
            icon="fas fa-plus"
            onClick={handleOpenEntryModal}
            disabled={isSubmitting || !userData}
          >
            Add Entry
          </Button>
        </div>
      </Card>

      {/* ADMIN VIEW */}
      {isAdmin && (
        <>
          {/* Pending Entries Section */}
          <Card style={{ marginBottom: '2rem' }}>
            <h2 style={{
              margin: '0 0 1rem 0',
              fontSize: 'var(--psyber-font-size-2xl)',
              color: 'var(--psyber-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-clock"></i>
              Pending Approvals
              {pendingEntries.length > 0 && (
                <span style={{
                  fontSize: 'var(--psyber-font-size-sm)',
                  background: 'var(--psyber-warning)',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: 'var(--psyber-radius-full)',
                  fontWeight: 600
                }}>
                  {pendingEntries.length}
                </span>
              )}
            </h2>

            {pendingEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <i className="fas fa-inbox" style={{ fontSize: '3rem', color: 'var(--psyber-text-muted)', marginBottom: '1rem', display: 'block' }}></i>
                <p style={{ color: 'var(--psyber-text-secondary)' }}>No pending timesheets to review</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '1rem'
              }}>
                {pendingEntries.map((entry) => (
                  <TimesheetCard
                    key={entry.id}
                    entry={entry}
                    isAdmin={true}
                    isOwnEntry={entry.employeeId === userData.employeeId}
                    onApprove={handleApprove}
                    onReject={handleOpenRejectModal}
                    onEdit={handleEditEntry}
                    onCancel={handleCancel}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* View Mode Toggle */}
          <Card style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant={viewMode === 'list' ? 'primary' : 'ghost'}
                icon="fas fa-list"
                onClick={() => setViewMode('list')}
              >
                List View
              </Button>
              <Button
                variant={viewMode === 'monthly' ? 'primary' : 'ghost'}
                icon="fas fa-calendar-alt"
                onClick={() => setViewMode('monthly')}
              >
                Monthly View
              </Button>
            </div>
          </Card>

          {/* All Entries Section with Filters */}
          <Card>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: 'var(--psyber-font-size-2xl)',
                color: 'var(--psyber-text)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <i className="fas fa-list"></i>
                All Timesheets
              </h2>

              {/* Filters and Export */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {/* Status Filter */}
                <div>
                  <label
                    htmlFor="statusFilter"
                    style={{
                      display: 'block',
                      marginBottom: '0.25rem',
                      fontSize: 'var(--psyber-font-size-xs)',
                      color: 'var(--psyber-text-secondary)',
                      fontWeight: 600
                    }}
                  >
                    Status
                  </label>
                  <select
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="psyber-input"
                    style={{ cursor: 'pointer', minWidth: '150px' }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Employee Filter */}
                <div>
                  <label
                    htmlFor="employeeFilter"
                    style={{
                      display: 'block',
                      marginBottom: '0.25rem',
                      fontSize: 'var(--psyber-font-size-xs)',
                      color: 'var(--psyber-text-secondary)',
                      fontWeight: 600
                    }}
                  >
                    Employee
                  </label>
                  <select
                    id="employeeFilter"
                    value={employeeFilter}
                    onChange={(e) => setEmployeeFilter(e.target.value)}
                    className="psyber-input"
                    style={{ cursor: 'pointer', minWidth: '200px' }}
                  >
                    <option value="all">All Employees</option>
                    {employees.map(emp => (
                      <option key={emp.employeeId} value={emp.employeeId}>
                        {emp.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Export Button */}
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    variant="secondary"
                    icon="fas fa-download"
                    onClick={handleExportCSV}
                    disabled={isExporting || filteredAllEntries.length === 0}
                    loading={isExporting}
                  >
                    {isExporting ? 'Exporting...' : 'Export to CSV'}
                  </Button>
                </div>
              </div>
            </div>

{viewMode === 'list' ? (
              // List View
              filteredAllEntries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <i className="fas fa-filter" style={{ fontSize: '3rem', color: 'var(--psyber-text-muted)', marginBottom: '1rem', display: 'block' }}></i>
                  <p style={{ color: 'var(--psyber-text-secondary)' }}>No timesheets match the current filters</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1rem'
                }}>
                  {filteredAllEntries.map((entry) => (
                    <TimesheetCard
                      key={entry.id}
                      entry={entry}
                      isAdmin={true}
                      isOwnEntry={entry.employeeId === userData.employeeId}
                      onApprove={handleApprove}
                      onReject={handleOpenRejectModal}
                      onEdit={handleEditEntry}
                      onCancel={handleCancel}
                    />
                  ))}
                </div>
              )
            ) : (
              // Monthly View
              monthlyGroupedEntries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <i className="fas fa-calendar-alt" style={{ fontSize: '3rem', color: 'var(--psyber-text-muted)', marginBottom: '1rem', display: 'block' }}></i>
                  <p style={{ color: 'var(--psyber-text-secondary)' }}>No timesheets to display</p>
                </div>
              ) : (
                <div>
                  {monthlyGroupedEntries.map((month) => (
                    <div key={month.monthKey} style={{ marginBottom: '2rem' }}>
                      {/* Month Header */}
                      <h3 style={{
                        margin: '0 0 1rem 0',
                        fontSize: 'var(--psyber-font-size-xl)',
                        color: 'var(--psyber-text)',
                        fontWeight: 700,
                        padding: '0.75rem 0',
                        borderBottom: '2px solid var(--psyber-primary)'
                      }}>
                        {month.monthDisplay}
                      </h3>

                      {/* Employee Rows */}
                      {month.employees.map((employee) => {
                        const rowKey = `${month.monthKey}-${employee.employeeId}`
                        const isExpanded = isRowExpanded(month.monthKey, employee.employeeId)

                        return (
                          <div key={rowKey} style={{ marginBottom: '1rem' }}>
                            {/* Employee Header (Clickable) */}
                            <div
                              onClick={() => handleToggleRow(month.monthKey, employee.employeeId)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '1rem 1.5rem',
                                background: 'var(--psyber-card-hover)',
                                borderRadius: 'var(--psyber-radius-md)',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                border: '2px solid transparent',
                                ':hover': {
                                  background: 'var(--psyber-primary-opacity-10)',
                                  borderColor: 'var(--psyber-primary)'
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--psyber-primary-opacity-10)'
                                e.currentTarget.style.borderColor = 'var(--psyber-primary)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'var(--psyber-card-hover)'
                                e.currentTarget.style.borderColor = 'transparent'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <i
                                  className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}
                                  style={{
                                    color: 'var(--psyber-primary)',
                                    fontSize: 'var(--psyber-font-size-lg)',
                                    transition: 'transform 0.2s ease'
                                  }}
                                ></i>
                                <div>
                                  <h4 style={{
                                    margin: 0,
                                    fontSize: 'var(--psyber-font-size-lg)',
                                    color: 'var(--psyber-text)',
                                    fontWeight: 600
                                  }}>
                                    {employee.employeeName}
                                  </h4>
                                  <p style={{
                                    margin: '0.25rem 0 0 0',
                                    fontSize: 'var(--psyber-font-size-xs)',
                                    color: 'var(--psyber-text-secondary)'
                                  }}>
                                    {employee.employeeId}
                                  </p>
                                </div>
                              </div>

                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                background: 'var(--psyber-primary-opacity-10)',
                                padding: '0.5rem 1rem',
                                borderRadius: 'var(--psyber-radius-full)',
                                border: '2px solid var(--psyber-primary)'
                              }}>
                                <i className="fas fa-clock" style={{ color: 'var(--psyber-primary)' }}></i>
                                <span style={{
                                  fontSize: 'var(--psyber-font-size-lg)',
                                  fontWeight: 700,
                                  color: 'var(--psyber-primary)'
                                }}>
                                  {employee.totalHours.toFixed(1)}h
                                </span>
                              </div>
                            </div>

                            {/* Expanded Entries */}
                            {isExpanded && (
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                                gap: '1rem',
                                marginTop: '1rem',
                                padding: '1rem',
                                background: 'var(--psyber-bg-secondary)',
                                borderRadius: 'var(--psyber-radius-md)',
                                border: '1px solid var(--psyber-card-border)'
                              }}>
                                {employee.entries.map((entry) => (
                                  <TimesheetCard
                                    key={entry.id}
                                    entry={entry}
                                    isAdmin={true}
                                    isOwnEntry={entry.employeeId === userData.employeeId}
                                    onApprove={handleApprove}
                                    onReject={handleOpenRejectModal}
                                    onEdit={handleEditEntry}
                                    onCancel={handleCancel}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              )
            )}
          </Card>
        </>
      )}

      {/* EMPLOYEE VIEW */}
      {!isAdmin && (
        <>
          {/* Weekly Summary */}
          {myEntries.length > 0 && (
            <TimesheetSummary
              entries={myEntries}
              employeeId={userData?.employeeId}
            />
          )}

          {/* Date Filter Card */}
          <Card style={{ marginBottom: '2rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: 'var(--psyber-font-size-2xl)',
                color: 'var(--psyber-text)'
              }}>
                My Timesheet Entries
              </h2>

              {/* Date Range Filter */}
              <div>
                <label
                  htmlFor="dateFilter"
                  style={{
                    display: 'block',
                    marginBottom: '0.25rem',
                    fontSize: 'var(--psyber-font-size-xs)',
                    color: 'var(--psyber-text-secondary)',
                    fontWeight: 600
                  }}
                >
                  Date Range
                </label>
                <select
                  id="dateFilter"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="psyber-input"
                  style={{ cursor: 'pointer', minWidth: '150px' }}
                >
                  <option value="last7">Last 7 Days</option>
                  <option value="last30">Last 30 Days</option>
                  <option value="last90">Last 3 Months</option>
                </select>
              </div>
            </div>
          </Card>

          {/* My Entries List */}
          <Card>
            {filteredMyEntries.length === 0 ? (
              <EmptyState />
            ) : (
              <div>
                {groupedEntries.map((group) => (
                  <div key={group.date} style={{ marginBottom: '2rem' }}>
                    {/* Date Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem 1rem',
                      background: 'var(--psyber-card-hover)',
                      borderRadius: 'var(--psyber-radius-md)',
                      marginBottom: '1rem'
                    }}>
                      <h3 style={{
                        margin: 0,
                        fontSize: 'var(--psyber-font-size-lg)',
                        color: 'var(--psyber-text)'
                      }}>
                        {formatDate(group.date)}
                      </h3>
                      <span style={{
                        fontSize: 'var(--psyber-font-size-sm)',
                        color: 'var(--psyber-text-secondary)',
                        fontWeight: 600
                      }}>
                        Total: {group.totalHours.toFixed(1)}h
                      </span>
                    </div>

                    {/* Entries Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                      gap: '1rem',
                      marginBottom: '1rem'
                    }}>
                      {group.entries.map((entry) => (
                        <TimesheetCard
                          key={entry.id}
                          entry={entry}
                          isAdmin={false}
                          isOwnEntry={true}
                          onEdit={handleEditEntry}
                          onCancel={handleCancel}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Entry Modal */}
      <TimesheetEntryModal
        isOpen={isEntryModalOpen}
        onClose={handleCloseEntryModal}
        onSubmit={handleSubmitEntry}
        userData={userData}
        isLoading={isSubmitting}
        editEntry={editingEntry}
        selectedDate={getTodayDate()}
      />

      {/* Reject Modal */}
      <RejectModal
        isOpen={isRejectModalOpen}
        onClose={handleCloseRejectModal}
        onSubmit={handleSubmitReject}
        entry={rejectingEntry}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default Timesheet
